import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, LogOut, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import AdminStats from "@/components/AdminStats";
import SourcesView from "@/components/SourcesView";
import AdminQuotes from "@/components/AdminQuotes";
import AdminBlogs from "@/components/AdminBlogs";
import { mergeContent } from "@/context/ContentProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "scheduled", "completed"];
const TABS = ["Leads", "Quotes", "Stats", "Site Content", "Before & After", "Blogs"];

function formatDetail(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

const Field = ({ label, value, onChange, multiline, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">{label}</span>
    {multiline ? (
      <textarea className="input-dark mt-1.5 min-h-[90px] resize-y" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    ) : (
      <input className="input-dark mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    )}
  </label>
);

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("Leads");
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [baTitle, setBaTitle] = useState("");
  const [baBefore, setBaBefore] = useState(null);
  const [baAfter, setBaAfter] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const seenLeads = useRef(null);

  const authFetch = (url, options = {}) => fetch(url, { credentials: "include", ...options });

  const loadLeads = useCallback(async () => {
    const res = await authFetch(`${API}/admin/leads`);
    if (!res.ok) return;
    const data = await res.json();
    if (seenLeads.current) {
      data.forEach((l) => {
        if (!seenLeads.current.has(l.id)) toast.info(`New consultation request from ${l.name}`);
      });
    }
    seenLeads.current = new Set(data.map((l) => l.id));
    setLeads(data);
  }, []);

  const loadPairs = useCallback(async () => {
    const res = await fetch(`${API}/before-after`);
    if (res.ok) setPairs(await res.json());
  }, []);

  const loadContent = useCallback(async () => {
    const res = await fetch(`${API}/content`);
    const data = res.ok ? await res.json() : {};
    const merged = mergeContent(data && Object.keys(data).length ? data : {});
    setDraft({
      texts: merged.texts,
      services: merged.services.map(({ slug, name, tagline, description, image, benefits }) => ({ slug, name, tagline, description, image, benefits })),
      gallery: merged.gallery,
      phones: merged.phones,
      heroImage: merged.heroImage,
    });
  }, []);

  useEffect(() => {
    authFetch(`${API}/auth/me`)
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => setUser(u || null))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    loadLeads();
    loadPairs();
    loadContent();
    const interval = setInterval(() => {
      loadLeads();
    }, 10000);
    return () => clearInterval(interval);
  }, [user, loadLeads, loadPairs, loadContent]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(formatDetail(data?.detail));
      }
      setUser(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await authFetch(`${API}/auth/logout`, { method: "POST" });
    setUser(null);
  };

  const updateStatus = async (id, status) => {
    const res = await authFetch(`${API}/admin/leads/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (res.ok) setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const deleteLead = async (id, name) => {
    if (!window.confirm(`Delete the lead from ${name}? This can't be undone.`)) return;
    const res = await authFetch(`${API}/admin/leads/${id}`, { method: "DELETE" });
    if (res.ok) {
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error("Save failed");
      toast.success("Site content saved — changes are live on the site.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadPair = async (e) => {
    e.preventDefault();
    if (!baBefore || !baAfter) {
      toast.error("Choose both a before and an after photo.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", baTitle);
      fd.append("before", baBefore);
      fd.append("after", baAfter);
      const res = await authFetch(`${API}/admin/before-after`, { method: "POST", body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(formatDetail(data?.detail));
      }
      toast.success("Before & after pair published to the gallery.");
      setBaTitle("");
      setBaBefore(null);
      setBaAfter(null);
      e.target.reset();
      loadPairs();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const deletePair = async (id) => {
    const res = await authFetch(`${API}/admin/before-after/${id}`, { method: "DELETE" });
    if (res.ok) {
      setPairs((prev) => prev.filter((p) => p.id !== id));
      toast.success("Pair removed.");
    }
  };

  const setText = (key, value) => setDraft((d) => ({ ...d, texts: { ...d.texts, [key]: value } }));
  const setWhyCard = (i, key, value) =>
    setDraft((d) => {
      const whyCards = d.texts.whyCards.map((c, idx) => (idx === i ? { ...c, [key]: value } : c));
      return { ...d, texts: { ...d.texts, whyCards } };
    });
  const setService = (slug, key, value) =>
    setDraft((d) => ({ ...d, services: d.services.map((s) => (s.slug === slug ? { ...s, [key]: value } : s)) }));
  const setGalleryItem = (i, key, value) =>
    setDraft((d) => ({ ...d, gallery: d.gallery.map((g, idx) => (idx === i ? { ...g, [key]: value } : g)) }));

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="w-6 h-6 text-amber-700 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] px-4" data-testid="admin-login-page">
        <Seo title="Admin Login | Crownvera Epoxy" description="Crownvera Epoxy admin login." noindex />
        <form onSubmit={login} className="w-full max-w-sm gold-ring-card rounded-xl p-8" data-testid="admin-login-form">
          <h1 className="font-display text-2xl text-stone-900 mb-1">Crownvera Admin</h1>
          <p className="text-sm text-stone-500 mb-6">crownveraepoxy.com dashboard</p>
          {error && <p className="text-sm text-red-600 mb-4" data-testid="admin-login-error">{error}</p>}
          <div className="space-y-4">
            <input className="input-dark" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
            <input className="input-dark" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm py-3.5 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] px-4 sm:px-8 py-10" data-testid="admin-dashboard">
      <Seo title="Admin | Crownvera Epoxy" description="Crownvera Epoxy admin." noindex />
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h1 className="font-display text-3xl text-stone-900">Crownvera Admin</h1>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-stone-500 hover:text-amber-700 transition-colors duration-200">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setSourcesOpen(false); }}
              data-testid={`admin-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`text-xs uppercase tracking-wider font-semibold px-4 py-2 rounded-full border transition-colors duration-300 ${
                tab === t ? "bg-amber-600 text-white border-amber-600 shadow-md" : "border-amber-600/25 bg-white text-stone-500 hover:text-amber-800"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Leads" && (
          <div data-testid="admin-leads-tab">
            <p className="text-sm text-stone-500 mb-6" data-testid="admin-leads-count">
              {leads.length} total · {leads.filter((l) => l.status === "new").length} new — new requests pop up here automatically.
            </p>
            {leads.length === 0 ? (
              <p className="text-stone-500 text-center py-20" data-testid="admin-leads-empty">No leads yet. New consultation requests will appear here automatically.</p>
            ) : (
              <div className="space-y-4">
                {leads.map((lead) => (
                  <div key={lead.id} className="gold-ring-card rounded-lg p-6" data-testid={`admin-lead-${lead.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-lg text-stone-900 font-semibold">{lead.name}</p>
                        <a href={`tel:${lead.phone}`} data-testid={`admin-lead-phone-${lead.id}`} className="flex items-center gap-2 text-sm text-amber-700 mt-1">
                          <Phone className="w-3.5 h-3.5" /> {lead.phone}
                        </a>
                        {lead.email && <p className="text-sm text-stone-500 mt-1">{lead.email}</p>}
                        {lead.zip && <p className="text-sm text-stone-500 mt-1" data-testid={`admin-lead-zip-${lead.id}`}>Zip: {lead.zip}</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        <select
                          value={lead.status}
                          onChange={(e) => updateStatus(lead.id, e.target.value)}
                          data-testid={`admin-lead-status-${lead.id}`}
                          className="input-dark !w-auto text-sm capitalize"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} className="capitalize">{s}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => deleteLead(lead.id, lead.name)}
                          data-testid={`admin-lead-delete-${lead.id}`}
                          aria-label="Delete lead"
                          className="text-stone-400 hover:text-red-600 transition-colors duration-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {(lead.service || lead.message) && (
                      <div className="mt-4 text-sm text-stone-600 space-y-1">
                        {lead.service && <p><span className="text-stone-400">Service:</span> {lead.service}</p>}
                        {lead.message && <p><span className="text-stone-400">Message:</span> {lead.message}</p>}
                      </div>
                    )}
                    <p className="mt-3 text-xs text-stone-400">{new Date(lead.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "Quotes" && <AdminQuotes />}

        {tab === "Stats" && (
          sourcesOpen
            ? <SourcesView onBack={() => setSourcesOpen(false)} />
            : <AdminStats onViewAll={() => setTab("Leads")} onShowSources={() => setSourcesOpen(true)} />
        )}

        {tab === "Site Content" && draft && (
          <div className="space-y-8" data-testid="admin-content-tab">
            <section className="gold-ring-card rounded-lg p-6 space-y-4">
              <h2 className="font-display text-xl text-amber-900">Homepage Hero</h2>
              <Field label="Eyebrow (small line above headline)" value={draft.texts.heroEyebrow} onChange={(v) => setText("heroEyebrow", v)} testid="content-hero-eyebrow" />
              <Field label="Headline" value={draft.texts.heroHeadline} onChange={(v) => setText("heroHeadline", v)} testid="content-hero-headline" />
              <Field label="Subtext" value={draft.texts.heroSubcopy} onChange={(v) => setText("heroSubcopy", v)} multiline testid="content-hero-subcopy" />
              <Field label="Hero background image URL" value={draft.heroImage} onChange={(v) => setDraft((d) => ({ ...d, heroImage: v }))} testid="content-hero-image" />
            </section>

            <section className="gold-ring-card rounded-lg p-6 space-y-4">
              <h2 className="font-display text-xl text-amber-900">Why Epoxy Section</h2>
              <Field label="Heading" value={draft.texts.whyHeading} onChange={(v) => setText("whyHeading", v)} testid="content-why-heading" />
              <Field label="Intro" value={draft.texts.whyIntro} onChange={(v) => setText("whyIntro", v)} multiline testid="content-why-intro" />
              {draft.texts.whyCards.map((c, i) => (
                <div key={i} className="border border-amber-600/15 rounded-md p-4 space-y-3">
                  <Field label={`Reason ${i + 1} title`} value={c.title} onChange={(v) => setWhyCard(i, "title", v)} testid={`content-why-card-${i}-title`} />
                  <Field label={`Reason ${i + 1} text`} value={c.text} onChange={(v) => setWhyCard(i, "text", v)} multiline testid={`content-why-card-${i}-text`} />
                </div>
              ))}
            </section>

            <section className="gold-ring-card rounded-lg p-6 space-y-4">
              <h2 className="font-display text-xl text-amber-900">Consultation Section</h2>
              <Field label="Heading" value={draft.texts.consultHeading} onChange={(v) => setText("consultHeading", v)} testid="content-consult-heading" />
              <Field label="Text" value={draft.texts.consultText} onChange={(v) => setText("consultText", v)} multiline testid="content-consult-text" />
            </section>

            <section className="gold-ring-card rounded-lg p-6 space-y-4">
              <h2 className="font-display text-xl text-amber-900">Phone Numbers</h2>
              {draft.phones.map((p, i) => (
                <Field key={i} label={`Phone ${i + 1}`} value={p} onChange={(v) => setDraft((d) => ({ ...d, phones: d.phones.map((x, idx) => (idx === i ? v : x)) }))} testid={`content-phone-${i}`} />
              ))}
            </section>

            <section className="gold-ring-card rounded-lg p-6 space-y-4">
              <h2 className="font-display text-xl text-amber-900">Services</h2>
              {draft.services.map((s) => (
                <details key={s.slug} className="border border-amber-600/15 rounded-md p-4" data-testid={`content-service-${s.slug}`}>
                  <summary className="cursor-pointer font-semibold text-stone-800 text-sm">{s.name}</summary>
                  <div className="mt-4 space-y-3">
                    <Field label="Name" value={s.name} onChange={(v) => setService(s.slug, "name", v)} testid={`content-service-${s.slug}-name`} />
                    <Field label="Tagline" value={s.tagline} onChange={(v) => setService(s.slug, "tagline", v)} testid={`content-service-${s.slug}-tagline`} />
                    <Field label="Description" value={s.description} onChange={(v) => setService(s.slug, "description", v)} multiline testid={`content-service-${s.slug}-description`} />
                    <Field label="Photo URL" value={s.image} onChange={(v) => setService(s.slug, "image", v)} testid={`content-service-${s.slug}-image`} />
                    <Field
                      label="Benefits (comma separated)"
                      value={(s.benefits || []).join(", ")}
                      onChange={(v) => setService(s.slug, "benefits", v.split(",").map((b) => b.trim()).filter(Boolean))}
                      testid={`content-service-${s.slug}-benefits`}
                    />
                  </div>
                </details>
              ))}
            </section>

            <section className="gold-ring-card rounded-lg p-6 space-y-4">
              <h2 className="font-display text-xl text-amber-900">Gallery Photos</h2>
              {draft.gallery.map((g, i) => (
                <div key={i} className="border border-amber-600/15 rounded-md p-4 space-y-3" data-testid={`content-gallery-${i}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Photo {i + 1}</span>
                    <button
                      onClick={() => setDraft((d) => ({ ...d, gallery: d.gallery.filter((_, idx) => idx !== i) }))}
                      data-testid={`content-gallery-${i}-remove`}
                      className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                  <Field label="Title" value={g.title} onChange={(v) => setGalleryItem(i, "title", v)} testid={`content-gallery-${i}-title`} />
                  <Field label="Category" value={g.category} onChange={(v) => setGalleryItem(i, "category", v)} testid={`content-gallery-${i}-category`} />
                  <Field label="Photo URL" value={g.url} onChange={(v) => setGalleryItem(i, "url", v)} testid={`content-gallery-${i}-url`} />
                </div>
              ))}
              <button
                onClick={() => setDraft((d) => ({ ...d, gallery: [...d.gallery, { title: "", category: "Metallic Finishes", url: "" }] }))}
                data-testid="content-gallery-add"
                className="text-xs uppercase tracking-wider font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-1.5"
              >
                <ImagePlus className="w-4 h-4" /> Add photo
              </button>
            </section>

            <button
              onClick={saveContent}
              disabled={saving}
              data-testid="content-save-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? "Saving..." : "Save All Changes"}
            </button>
          </div>
        )}

        {tab === "Blogs" && <AdminBlogs />}

        {tab === "Before & After" && (
          <div className="space-y-8" data-testid="admin-before-after-tab">
            <form onSubmit={uploadPair} className="gold-ring-card rounded-lg p-6 space-y-4" data-testid="ba-upload-form">
              <h2 className="font-display text-xl text-amber-900">Upload a Before &amp; After Pair</h2>
              <Field label="Project title (optional)" value={baTitle} onChange={setBaTitle} testid="ba-title-input" />
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Before photo *</span>
                  <input type="file" accept="image/*" required onChange={(e) => setBaBefore(e.target.files[0])} className="input-dark mt-1.5 file:mr-3 file:text-amber-700 file:font-semibold file:border-0 file:bg-transparent" data-testid="ba-before-input" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">After photo *</span>
                  <input type="file" accept="image/*" required onChange={(e) => setBaAfter(e.target.files[0])} className="input-dark mt-1.5 file:mr-3 file:text-amber-700 file:font-semibold file:border-0 file:bg-transparent" data-testid="ba-after-input" />
                </label>
              </div>
              <button
                type="submit"
                disabled={uploading}
                data-testid="ba-upload-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-8 py-3.5 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center gap-2"
              >
                {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                {uploading ? "Uploading..." : "Publish to Gallery"}
              </button>
            </form>

            {pairs.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4">
                {pairs.map((p) => (
                  <div key={p.id} className="gold-ring-card rounded-lg p-4" data-testid={`ba-pair-${p.id}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${p.before_url}`} alt="Before" className="rounded-md h-32 w-full object-cover" />
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${p.after_url}`} alt="After" className="rounded-md h-32 w-full object-cover" />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-sm text-stone-700 font-medium">{p.title || "Untitled project"}</p>
                      <button onClick={() => deletePair(p.id)} data-testid={`ba-delete-${p.id}`} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
